import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Typography,
  Box,
  Paper,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  CircularProgress,
  Alert,
  Fade,
  useTheme,
  Divider,
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as BackIcon,
  Person as PersonIcon,
  DirectionsCar as CarIcon,
  Payment as PaymentIcon,
} from '@mui/icons-material';
import { format, parseISO, differenceInCalendarDays } from 'date-fns';
import {
  Vehicle,
  VehicleRentalBillingType,
  PaymentMethod,
  PAYMENT_METHODS,
  PaymentStatus,
  PAYMENT_STATUS,
} from '../../types';
import vehicleService from '../../services/vehicleService';
import vehicleRentalService from '../../services/vehicleRentalService';

interface FormState {
  vehicle_id: number;
  billing_type: VehicleRentalBillingType;
  start_date: string;
  end_date: string;
  daily_rate: string;
  monthly_amount: string;
  renter_name: string;
  renter_phone: string;
  renter_document: string;
  renter_email: string;
  renter_zip: string;
  renter_address: string;
  renter_number: string;
  renter_complement: string;
  renter_neighborhood: string;
  renter_city: string;
  renter_state: string;
  payment_method: PaymentMethod;
  payment_status: PaymentStatus;
  notes: string;
}

const emptyForm: FormState = {
  vehicle_id: 0,
  billing_type: 'daily',
  start_date: format(new Date(), 'yyyy-MM-dd'),
  end_date: format(new Date(), 'yyyy-MM-dd'),
  daily_rate: '',
  monthly_amount: '',
  renter_name: '',
  renter_phone: '',
  renter_document: '',
  renter_email: '',
  renter_zip: '',
  renter_address: '',
  renter_number: '',
  renter_complement: '',
  renter_neighborhood: '',
  renter_city: '',
  renter_state: '',
  payment_method: 'pix',
  payment_status: 'pending',
  notes: '',
};

const RentalForm: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = Boolean(id);

  const [form, setForm] = useState<FormState>(emptyForm);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await vehicleService.getAvailableVehicles();
        if (!cancelled) setVehicles(list);
      } catch (e) {
        console.error(e);
        if (!cancelled) setLoadError('Não foi possível carregar a lista de veículos.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isEditing || !id) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const r = await vehicleRentalService.getById(Number(id));
        if (cancelled) return;
        setForm({
          vehicle_id: r.vehicle_id,
          billing_type: r.billing_type,
          start_date: r.start_date?.length >= 10 ? r.start_date.slice(0, 10) : r.start_date,
          end_date: r.end_date ? (r.end_date.length >= 10 ? r.end_date.slice(0, 10) : r.end_date) : '',
          daily_rate: r.daily_rate != null ? String(r.daily_rate) : '',
          monthly_amount: r.monthly_amount != null ? String(r.monthly_amount) : '',
          renter_name: r.renter_name || '',
          renter_phone: r.renter_phone || '',
          renter_document: r.renter_document || '',
          renter_email: r.renter_email || '',
          renter_zip: r.renter_zip || '',
          renter_address: r.renter_address || '',
          renter_number: r.renter_number || '',
          renter_complement: r.renter_complement || '',
          renter_neighborhood: r.renter_neighborhood || '',
          renter_city: r.renter_city || '',
          renter_state: r.renter_state || '',
          payment_method: r.payment_method || 'pix',
          payment_status: r.payment_status || 'pending',
          notes: r.notes || '',
        });
        if (r.vehicle) setSelectedVehicle(r.vehicle);
        else setSelectedVehicle(null);
      } catch (e) {
        console.error(e);
        if (!cancelled) setLoadError('Locação não encontrada ou API indisponível.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEditing, id]);

  useEffect(() => {
    if (!form.vehicle_id || vehicles.length === 0) return;
    if (selectedVehicle?.id === form.vehicle_id) return;
    const v = vehicles.find((x) => x.id === form.vehicle_id);
    if (v) setSelectedVehicle(v);
  }, [vehicles, form.vehicle_id, selectedVehicle?.id]);

  const numberOfDays = useMemo(() => {
    if (form.billing_type !== 'daily' || !form.start_date || !form.end_date) return null;
    try {
      const a = parseISO(`${form.start_date}T12:00:00`);
      const b = parseISO(`${form.end_date}T12:00:00`);
      if (b < a) return null;
      return differenceInCalendarDays(b, a) + 1;
    } catch {
      return null;
    }
  }, [form.billing_type, form.start_date, form.end_date]);

  const previewTotalDaily = useMemo(() => {
    if (form.billing_type !== 'daily' || numberOfDays == null) return null;
    const rate = parseFloat(String(form.daily_rate).replace(',', '.'));
    if (!Number.isFinite(rate) || rate < 0) return null;
    return rate * numberOfDays;
  }, [form.billing_type, form.daily_rate, numberOfDays]);

  const vehicleOptions = useMemo(() => {
    if (selectedVehicle && !vehicles.some((x) => x.id === selectedVehicle.id)) {
      return [selectedVehicle, ...vehicles];
    }
    return vehicles;
  }, [vehicles, selectedVehicle]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!form.vehicle_id) {
      setError('Selecione um veículo.');
      return;
    }
    if (!form.renter_name.trim() || !form.renter_phone.trim() || !form.renter_document.trim()) {
      setError('Preencha nome, telefone e documento do locatário.');
      return;
    }
    if (form.billing_type === 'daily') {
      if (!form.start_date || !form.end_date) {
        setError('Informe início e fim do período (diárias).');
        return;
      }
      const rate = parseFloat(String(form.daily_rate).replace(',', '.'));
      if (!Number.isFinite(rate) || rate <= 0) {
        setError('Informe o valor da diária.');
        return;
      }
      if (numberOfDays == null || numberOfDays < 1) {
        setError('A data final deve ser igual ou posterior à inicial.');
        return;
      }
    } else {
      const monthly = parseFloat(String(form.monthly_amount).replace(',', '.'));
      if (!Number.isFinite(monthly) || monthly <= 0) {
        setError('Informe o valor da mensalidade.');
        return;
      }
      if (!form.start_date) {
        setError('Informe o início do contrato.');
        return;
      }
    }

    const payload: Record<string, unknown> = {
      vehicle_id: form.vehicle_id,
      billing_type: form.billing_type,
      start_date: form.start_date,
      end_date: form.billing_type === 'daily' ? form.end_date : form.end_date || null,
      renter_name: form.renter_name.trim(),
      renter_phone: form.renter_phone.trim(),
      renter_document: form.renter_document.trim(),
      renter_email: form.renter_email.trim() || undefined,
      renter_zip: form.renter_zip.trim() || undefined,
      renter_address: form.renter_address.trim() || undefined,
      renter_number: form.renter_number.trim() || undefined,
      renter_complement: form.renter_complement.trim() || undefined,
      renter_neighborhood: form.renter_neighborhood.trim() || undefined,
      renter_city: form.renter_city.trim() || undefined,
      renter_state: form.renter_state.trim() || undefined,
      payment_method: form.payment_method,
      payment_status: form.payment_status,
      notes: form.notes.trim() || undefined,
    };
    if (form.billing_type === 'daily') {
      payload.daily_rate = parseFloat(String(form.daily_rate).replace(',', '.'));
      payload.number_of_days = numberOfDays;
    } else {
      payload.monthly_amount = parseFloat(String(form.monthly_amount).replace(',', '.'));
    }

    setSaving(true);
    try {
      if (isEditing && id) {
        await vehicleRentalService.update(Number(id), payload as any);
        navigate(`/locacoes/${id}`);
      } else {
        const created = await vehicleRentalService.create(payload as any);
        navigate(`/locacoes/${created.id}`);
      }
    } catch (err) {
      console.error(err);
      setError('Não foi possível salvar. Verifique os dados e a API.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress sx={{ color: theme.palette.primary.main }} />
      </Box>
    );
  }

  return (
    <Fade in>
      <Box component="form" onSubmit={handleSubmit} noValidate>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/locacoes')} sx={{ mb: 2 }} color="inherit">
          Voltar
        </Button>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', mb: 3 }}>
          {isEditing ? 'Editar locação' : 'Nova locação'}
        </Typography>

        {loadError && (
          <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
            {loadError}
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 2, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <CarIcon color="primary" /> Veículo e cobrança
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={vehicleOptions}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                getOptionLabel={(v) => `${v.brand} ${v.model} (${v.year}) — ${v.plate || v.identification || 's/ placa'}`}
                value={selectedVehicle}
                onChange={(_, v) => {
                  setSelectedVehicle(v);
                  setForm((p) => ({ ...p, vehicle_id: v?.id || 0 }));
                }}
                renderInput={(params) => <TextField {...params} label="Veículo" required placeholder="Buscar na frota disponível" />}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Tipo de cobrança</InputLabel>
                <Select
                  label="Tipo de cobrança"
                  value={form.billing_type}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, billing_type: e.target.value as VehicleRentalBillingType }))
                  }
                >
                  <MenuItem value="daily">Diárias</MenuItem>
                  <MenuItem value="monthly">Mensalidade</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {form.billing_type === 'daily' ? (
              <>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Retirada"
                    InputLabelProps={{ shrink: true }}
                    value={form.start_date}
                    onChange={(e) => setForm((p) => ({ ...p, start_date: e.target.value }))}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Devolução"
                    InputLabelProps={{ shrink: true }}
                    value={form.end_date}
                    onChange={(e) => setForm((p) => ({ ...p, end_date: e.target.value }))}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    label="Valor da diária (R$)"
                    type="number"
                    inputProps={{ min: 0, step: '0.01' }}
                    value={form.daily_rate}
                    onChange={(e) => setForm((p) => ({ ...p, daily_rate: e.target.value }))}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    {numberOfDays != null
                      ? `${numberOfDays} diária(s) — total estimado: ${previewTotalDaily != null ? previewTotalDaily.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '—'}`
                      : 'Ajuste as datas para calcular o total.'}
                  </Typography>
                </Grid>
              </>
            ) : (
              <>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Início do contrato"
                    InputLabelProps={{ shrink: true }}
                    value={form.start_date}
                    onChange={(e) => setForm((p) => ({ ...p, start_date: e.target.value }))}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Fim do contrato (opcional)"
                    InputLabelProps={{ shrink: true }}
                    value={form.end_date}
                    onChange={(e) => setForm((p) => ({ ...p, end_date: e.target.value }))}
                    helperText="Deixe em branco se a vigência for indefinida até cancelamento."
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    label="Valor mensal (R$)"
                    type="number"
                    inputProps={{ min: 0, step: '0.01' }}
                    value={form.monthly_amount}
                    onChange={(e) => setForm((p) => ({ ...p, monthly_amount: e.target.value }))}
                    required
                  />
                </Grid>
              </>
            )}
          </Grid>
        </Paper>

        <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 2, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonIcon color="primary" /> Locatário
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nome completo"
                value={form.renter_name}
                onChange={(e) => setForm((p) => ({ ...p, renter_name: e.target.value }))}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Telefone"
                value={form.renter_phone}
                onChange={(e) => setForm((p) => ({ ...p, renter_phone: e.target.value }))}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="CPF / CNPJ"
                value={form.renter_document}
                onChange={(e) => setForm((p) => ({ ...p, renter_document: e.target.value }))}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="E-mail (opcional)"
                type="email"
                value={form.renter_email}
                onChange={(e) => setForm((p) => ({ ...p, renter_email: e.target.value }))}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
                Endereço (opcional)
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="CEP"
                value={form.renter_zip}
                onChange={(e) => setForm((p) => ({ ...p, renter_zip: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={7}>
              <TextField
                fullWidth
                label="Rua / Logradouro"
                value={form.renter_address}
                onChange={(e) => setForm((p) => ({ ...p, renter_address: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                label="Número"
                value={form.renter_number}
                onChange={(e) => setForm((p) => ({ ...p, renter_number: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Complemento"
                value={form.renter_complement}
                onChange={(e) => setForm((p) => ({ ...p, renter_complement: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Bairro"
                value={form.renter_neighborhood}
                onChange={(e) => setForm((p) => ({ ...p, renter_neighborhood: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Cidade"
                value={form.renter_city}
                onChange={(e) => setForm((p) => ({ ...p, renter_city: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={1}>
              <TextField
                fullWidth
                label="UF"
                value={form.renter_state}
                onChange={(e) => setForm((p) => ({ ...p, renter_state: e.target.value.toUpperCase().slice(0, 2) }))}
                inputProps={{ maxLength: 2 }}
              />
            </Grid>
          </Grid>
        </Paper>

        <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 2, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <PaymentIcon color="primary" /> Pagamento
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <InputLabel>Forma de pagamento</InputLabel>
                <Select
                  label="Forma de pagamento"
                  value={form.payment_method}
                  onChange={(e) => setForm((p) => ({ ...p, payment_method: e.target.value as PaymentMethod }))}
                >
                  {(Object.keys(PAYMENT_METHODS) as PaymentMethod[]).map((k) => (
                    <MenuItem key={k} value={k}>
                      {PAYMENT_METHODS[k]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <InputLabel>Status do pagamento</InputLabel>
                <Select
                  label="Status do pagamento"
                  value={form.payment_status}
                  onChange={(e) => setForm((p) => ({ ...p, payment_status: e.target.value as PaymentStatus }))}
                >
                  {(Object.keys(PAYMENT_STATUS) as PaymentStatus[]).map((k) => (
                    <MenuItem key={k} value={k}>
                      {PAYMENT_STATUS[k]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Observações"
                multiline
                minRows={2}
                value={form.notes}
                onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
              />
            </Grid>
          </Grid>
        </Paper>

        <Divider sx={{ my: 2 }} />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button variant="outlined" onClick={() => navigate(isEditing && id ? `/locacoes/${id}` : '/locacoes')}>
            Cancelar
          </Button>
          <Button type="submit" variant="contained" startIcon={<SaveIcon />} disabled={saving}>
            {saving ? 'Salvando…' : 'Salvar'}
          </Button>
        </Box>
      </Box>
    </Fade>
  );
};

export default RentalForm;
